/**
 * Token Manager for Upload Service
 *
 * Manages fetching and caching of uploader JWT tokens.
 * The upload service requires a JWT token separate from the regular user token.
 */

import AXIOS from 'axios';
import endpoints from './endpoints.json';
import baseUrl from './baseURL.json';

const UPLOADER_TOKEN_KEY = 'uploader_token';
const TOKEN_EXPIRY_KEY = 'uploader_token_expiry';

// Get API base URL based on environment
const getApiBaseUrl = () => {
  if (typeof window === 'undefined') return baseUrl.PRODUCTION;

  const { origin } = window.location;

  if (origin.includes('localhost')) {
    return baseUrl.PRODUCTION; // Use production backend for localhost testing
  }
  if (origin.includes('preprod')) {
    return baseUrl.PRODUCTION;
  }
  if (origin.includes('dev.app')) {
    return baseUrl.DEVELOPMENT_URL;
  }
  return baseUrl.PRODUCTION;
};

// Create axios instance for API calls
const axios = AXIOS.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Check if token is expired or about to expire (within 5 minutes)
 */
function isTokenExpired() {
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return true;

  const expiry = parseInt(expiryStr, 10);
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  return expiry - now < fiveMinutes;
}

/**
 * Parse JWT token to extract expiry time
 * @param {string} token - JWT token
 * @returns {number|null} Expiry timestamp in milliseconds, or null if invalid
 */
function parseTokenExpiry(token) {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // 'exp' is in seconds, convert to milliseconds
    if (payload.exp) {
      return payload.exp * 1000;
    }

    return null;
  } catch (error) {
    console.error('[TokenManager] Failed to parse token expiry:', error);
    return null;
  }
}

/**
 * Validate token format (must be JWT)
 * @param {string} token - Token to validate
 * @returns {boolean} True if valid JWT format
 */
export function isValidJWT(token) {
  if (!token || typeof token !== 'string') return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    // Try to decode the payload
    const payload = JSON.parse(atob(parts[1]));
    return !!payload;
  } catch {
    return false;
  }
}

/**
 * Fetch a new uploader token from the backend
 *
 * The upload token is returned in the `api_response.access` field from:
 * - POST /v4/signin (during login)
 * - POST /getMyProfile (when fetching user profile)
 *
 * This matches the mobile app's approach where upload token comes from signin response.
 *
 * @returns {Promise<string>} JWT token for upload service
 */
async function fetchUploaderToken() {
  try {
    console.log('[TokenManager] Fetching uploader token from backend...');

    // Check if user is logged in
    const userToken = localStorage.getItem('token');
    if (!userToken) {
      throw new Error('No user token found. Please log in first.');
    }

    // Primary: Use /auth/get-token/ on cloud/uploader service (same as gift flow success page)
    try {
      const cloudBaseUrl = window.location.origin.includes('dev.app')
        ? 'https://cloud.eq7.in/api'
        : 'https://uploader.projector.app/api';
      const cloudResponse = await AXIOS.post(
        `${cloudBaseUrl}/auth/get-token/`,
        { token: userToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      if (cloudResponse?.data?.access) {
        console.log('[TokenManager] Upload token fetched via /auth/get-token/');
        return cloudResponse.data.access;
      }
    } catch (cloudError) {
      console.warn('[TokenManager] /auth/get-token/ failed, trying getMyProfile fallback:', cloudError.message);
    }

    // Fallback: Call getMyProfile to get fresh upload token from api_response.access
    // This matches mobile app pattern: accountService.dart:365-373
    const response = await axios.post(endpoints.getMyProfile, { token: userToken });

    console.log('[TokenManager] Profile response received');

    // Extract upload token from api_response.access (matches mobile app)
    const uploaderToken = response.data?.api_response?.access;

    if (!uploaderToken) {
      console.warn('[TokenManager] No api_response.access in profile response:', {
        hasApiResponse: !!response.data?.api_response,
        responseKeys: Object.keys(response.data || {}),
      });

      // Fallback: Try using user token if it's JWT format
      if (userToken.split('.').length === 3) {
        console.log('[TokenManager] Fallback: Using user token (JWT format)');
        return userToken;
      }

      throw new Error(
        'Upload token not found in api_response.access. Backend may need to be updated.'
      );
    }

    // Validate it's a JWT token
    if (!isValidJWT(uploaderToken)) {
      console.error('[TokenManager] Invalid upload token format:', uploaderToken.substring(0, 50));
      throw new Error('Upload token is not in JWT format');
    }

    console.log('[TokenManager] Upload token fetched successfully');

    // Also save user UUID if present (used for S3 paths)
    const userUuid = response.data?.api_response?.uuid;
    if (userUuid) {
      localStorage.setItem('user_uuid', userUuid);
      console.log('[TokenManager] User UUID saved:', userUuid);
    }

    return uploaderToken;
  } catch (error) {
    console.error('[TokenManager] Failed to fetch uploader token:', error);
    throw error;
  }
}

/**
 * Get valid uploader token (from cache or fetch new one)
 *
 * @returns {Promise<string>} Valid JWT token for upload service
 */
export async function getUploaderToken() {
  try {
    // Check if we have a cached token that's still valid
    const cachedToken = localStorage.getItem(UPLOADER_TOKEN_KEY);

    if (cachedToken && !isTokenExpired()) {
      console.log('[TokenManager] Using cached uploader token');
      return cachedToken;
    }

    // Fetch new token
    console.log('[TokenManager] Cached token expired or missing, fetching new one...');
    const newToken = await fetchUploaderToken();

    // Parse expiry from token
    const expiry = parseTokenExpiry(newToken);

    // Cache the new token
    localStorage.setItem(UPLOADER_TOKEN_KEY, newToken);
    if (expiry) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      console.log('[TokenManager] Token cached, expires at:', new Date(expiry).toISOString());
    }

    return newToken;
  } catch (error) {
    console.error('[TokenManager] Failed to get uploader token:', error);
    throw error;
  }
}

/**
 * Clear cached uploader token (call on logout or token error)
 */
export function clearUploaderToken() {
  localStorage.removeItem(UPLOADER_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  console.log('[TokenManager] Uploader token cleared');
}

export default { getUploaderToken, clearUploaderToken, isValidJWT };
